import * as React from 'react'
import { useState } from 'react'
import { Check, ChevronDown, Copy } from 'react-feather'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import styled, { useTheme } from 'styled-components'
import { Network, shortenAddress, getChainScanLink } from '@mprolab/sdk'
import { useMediaQuery } from '@mui/material'
import { getChainIconByNetwork } from '../../utils/getChainIconByNetwork'
import { Flex } from '../Flex'
import { Text, TextVariant } from '../Text'
import { Popover } from './Popover'

const SelectorBox = styled(Flex)`
  background: ${({ theme }) => theme.colors.black};
  padding: 4px 12px;
  width: max-content;
  height: max-content;
  justify-content: center;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  border-radius: 24px;
`

const StyledContainer = styled(Flex)`
  flex-direction: column;
  padding: 21px 25px;
  border: 1px solid ${({ theme }) => theme.colors.gray10};
  gap: 16px;
  border-radius: 8px;
  width: 211px;
`

const CopyIconWrapper = styled(Flex)`
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 0;
  align-items: center;
  justify-content: center;
`

const ExplorerLink = styled('a')`
  color: ${({ theme }) => theme.colors.white};
  text-decoration: none;
  flex: 1;
  min-width: 0;
  &:hover {
    text-decoration: underline;
  }
`

export interface NavbarListingContract {
  name: string
  address: string
  chain: Network
}

interface ContractSelectorProps {
  contracts: NavbarListingContract[]
}

const ContractSelector: React.FC<ContractSelectorProps> = ({ contracts }) => {
  const { colors, mediaQueries } = useTheme()
  const isMobile = useMediaQuery(mediaQueries.mobile)

  const initialContractsArray = contracts.map(() => false)
  const [copiedAddress, setCopiedAddress] = useState(initialContractsArray)
  const [isCopying, setIsCopying] = useState(false)

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const open = Boolean(anchorEl)

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget)
  }

  const handleCopy = (index: number) => {
    if (isCopying) return
    setIsCopying(true)
    const newStatus = [...copiedAddress]
    newStatus[index] = true
    setCopiedAddress(newStatus)
    setTimeout(() => {
      newStatus[index] = false
      setCopiedAddress([...newStatus])
      setIsCopying(false)
    }, 1000)
  }
  return (
    <Flex>
      <SelectorBox onClick={(e) => handleClick(e)}>
        <Text clickable variant={isMobile ? TextVariant.TEXT_XS_BOLD : TextVariant.BODY_SMALL_BOLD}>
          Show address
        </Text>
        <ChevronDown color={colors.white} />
      </SelectorBox>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 34,
          horizontal: -60
        }}
      >
        <StyledContainer>
          {contracts.map((contract, index) => {
            const { address, chain } = contract
            const explorerUrl = getChainScanLink({ data: address, type: 'token', network: chain })
            return (
              <Flex
                gap="16px"
                alignItems="center"
                justifyContent="space-between"
                key={`${chain}-${address}-${index}`}
              >
                {getChainIconByNetwork(chain)}
                <ExplorerLink href={explorerUrl} target="_blank" rel="noopener noreferrer">
                  <Text variant={TextVariant.BODY_SMALL} as="span">
                    {shortenAddress(address, 3, 5)}
                  </Text>
                </ExplorerLink>
                <CopyToClipboard text={address} onCopy={() => handleCopy(index)}>
                  <CopyIconWrapper as="button" type="button" aria-label="Copy contract address">
                    {copiedAddress[index] ? (
                      <Check color={colors.white} size={16} />
                    ) : (
                      <Copy color={colors.white} size={16} />
                    )}
                  </CopyIconWrapper>
                </CopyToClipboard>
              </Flex>
            )
          })}
        </StyledContainer>
      </Popover>
    </Flex>
  )
}

export default ContractSelector
